import classes from "./Signup.module.css";
import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { Button, Input, Text } from "components/common";
import LoginOrSignupWith from "../LoginOrSignupWith/LoginOrSignupWith";
import AuthenticationModal from "components/common/AuthenticationModal/AuthenticationModal";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

const Signup = ({ setIsLoggedIn, setReload }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState("");
  const [error, setError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.match(/[a-z]/)) strength += 1;
    if (password.match(/[A-Z]/)) strength += 1;
    if (password.match(/[0-9]/)) strength += 1;
    if (password.match(/[^a-zA-Z0-9]/)) strength += 1;
    return strength;
  };

  useEffect(() => {
    setCodeSent(false);
    setError(""); // Clear error when email changes
  }, [email]);

  useEffect(() => {
    // Validate passwords match whenever either password changes
    if (password && confirmPassword && password !== confirmPassword) {
      setPasswordMismatch("Passwords do not match.");
    } else {
      setPasswordMismatch("");
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    const validateUsername = async () => {
      if (!username) {
        setUsernameError("");
        setIsUsernameValid(false);
        return;
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}/validate-username`,
          {
            username,
          }
        );
        if (response.status === 200) {
          setUsernameError("");
          setIsUsernameValid(true);
        }
      } catch (error) {
        if (error.response?.status === 409) {
          setUsernameError("Username already in use");
          setIsUsernameValid(false);
        } else {
          setUsernameError("Failed to validate username");
          setIsUsernameValid(false);
        }
      }
    };

    // Clear error and validation state immediately when user types
    setUsernameError("");
    setIsUsernameValid(false);

    const timeoutId = setTimeout(validateUsername, 500); // Debounce for 500ms
    return () => clearTimeout(timeoutId);
  }, [username]);

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  async function sendCode(email) {
    try {
      setIsLoading(true);
      setError(""); // Clear any previous errors
      await axios.post(process.env.REACT_APP_API + "/request-signup-code", {
        email,
      });
      setCodeSent(true);
    } catch (error) {
      console.error("Error sending code:", error);
      if (error.response?.status === 409) {
        setError("Email already in use");
      } else if (error.response?.status === 400) {
        setError("Invalid email format");
      } else {
        setError("Failed to send code. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function submitHandler() {
    if (!agreeToTerms) {
      setTermsError("Please agree to the Terms and Conditions");
      return;
    }

    if (password !== confirmPassword) {
      setPasswordMismatch("Passwords do not match.");
      return;
    } else {
      setPasswordMismatch(""); // Clear any previous error if passwords match
    }

    if (email && !code) {
      return;
    }

    try {
      setIsLoading(true);
      setError(""); // Clear any previous errors
      const { data } = await axios.post(process.env.REACT_APP_API + "/signup", {
        username,
        email: email || null,
        password,
        code: code || null,
      });

      Cookies.set("token", data.token, { expires: 7 }); // Token expires in 7 days
      setIsLoggedIn(true);
      setReload((prev) => prev + 1);
      setShowSuccess(true);
    } catch (error) {
      console.error("Error signing up:", error);
      if (error.response?.status === 409) {
        setError("Email already in use");
      } else {
        setError("Failed to sign up. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  if (showSuccess) {
    return (
      <AuthenticationModal
        className={clsx(classes.modal, classes.myModal)}
        heading="Welcome to Teme UNS!"
        onClose={() => navigate("/chat")}
      >
        <div className={classes.formContainer}>
          <div className={classes.inputWrapper}>
            <Text PrimitiveTransparent60 lg className={classes.helperText}>
              Your account has been created successfully!
            </Text>
            <Button
              onClick={() => navigate("/chat")}
              wFull
              base
              className={classes.startChatButton}
            >
              Start Chatting Now
            </Button>
          </div>
        </div>
      </AuthenticationModal>
    );
  }

  return (
    <AuthenticationModal
      className={clsx(classes.modal, classes.myModal)}
      heading="Create your account"
      onClose={() => navigate(-1)}
    >
      <div className={classes.formContainer}>
        <div className={classes.inputWrapper}>
          <Text PrimitiveTransparent60 lg className={classes.helperText}>
            Welcome to Teme UNS! Let's get started
          </Text>
          <div className={classes.inputContainer}>
            <Input
              name="username"
              type="text"
              value={username}
              setValue={setUsername}
              placeholder="Username"
              className={usernameError ? classes.errorInput : ""}
            />
            {isUsernameValid && <span className={classes.checkmark}>✓</span>}
            {usernameError && <span className={classes.crossmark}>✕</span>}
          </div>
          {usernameError && (
            <Text className={classes.errorText} sm>
              {usernameError}
            </Text>
          )}
          <Input
            name="email"
            type="email"
            value={email}
            setValue={setEmail}
            placeholder="Email"
            className={error ? classes.errorInput : ""}
          />
          {error && (
            <Text className={classes.errorText} sm>
              {error}
            </Text>
          )}
          {/* <Text PrimitiveTransparent60 sm className={classes.helperText}>
            We recommend adding an email to recover your account if needed
          </Text> */}
          <Input
            name="password"
            type="password"
            value={password}
            setValue={setPassword}
            placeholder="Password"
            className={passwordMismatch ? classes.errorInput : ""}
          />
          {password && (
            <div className={classes.passwordStrengthContainer}>
              <div className={classes.strengthBars}>
                <div
                  className={`${classes.strengthBar} ${
                    passwordStrength >= 1 ? classes.active : ""
                  }`}
                />
                <div
                  className={`${classes.strengthBar} ${
                    passwordStrength >= 2 ? classes.active : ""
                  }`}
                />
                <div
                  className={`${classes.strengthBar} ${
                    passwordStrength >= 3 ? classes.active : ""
                  }`}
                />
                <div
                  className={`${classes.strengthBar} ${
                    passwordStrength >= 4 ? classes.active : ""
                  }`}
                />
                <div
                  className={`${classes.strengthBar} ${
                    passwordStrength >= 5 ? classes.active : ""
                  }`}
                />
              </div>
              <Text className={classes.strengthText} sm>
                {passwordStrength === 0 && "Very Weak"}
                {passwordStrength === 1 && "Weak"}
                {passwordStrength === 2 && "Fair"}
                {passwordStrength === 3 && "Good"}
                {passwordStrength === 4 && "Strong"}
                {passwordStrength === 5 && "Very Strong"}
              </Text>
            </div>
          )}
          <Input
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            setValue={setConfirmPassword}
            placeholder="Confirm New Password"
            className={passwordMismatch ? classes.errorInput : ""}
          />
          {passwordMismatch && (
            <Text className={classes.errorText} sm>
              {passwordMismatch}
            </Text>
          )}
          {codeSent && (
            <>
              <Text PrimitiveTransparent60 sm className={classes.helperText}>
                We've sent a verification code to {email}
              </Text>
              <Input
                name="setcode"
                type="number"
                value={code}
                setValue={setCode}
                placeholder="Enter Code"
              />
            </>
          )}

          <div className={classes.termsContainer}>
            <input
              type="checkbox"
              id="terms"
              checked={agreeToTerms}
              onChange={(e) => {
                setAgreeToTerms(e.target.checked);
                setTermsError(""); // Clear terms error when checkbox is clicked
              }}
              className={classes.checkbox}
            />
            <label htmlFor="terms" className={classes.termsLabel}>
              I agree to the{" "}
              <a href="/terms" className={classes.termsLink}>
                Terms and Conditions
              </a>
            </label>
          </div>
          {termsError && (
            <Text className={classes.errorText} sm>
              {termsError}
            </Text>
          )}
          {/* {!email && (
            <div className={classes.anonymousInfo}>
              <Text className={classes.infoText}>
                <span>•</span> Anonymous accounts provide a private, data-free
                experience
              </Text>
              <Text className={classes.infoText}>
                <span>•</span> Anonymous accounts require a paid subscription to
                access features
              </Text>
              <Text className={classes.infoText}>
                <span>•</span> Password cannot be reset for anonymous accounts
              </Text>
            </div>
          )} */}
          <Button
            loading={isLoading}
            onClick={() => {
              if (!email) {
                // submitHandler();
                alert("Enter Email");
                return;
              }
              if (!codeSent) {
                sendCode(email);
              } else {
                submitHandler();
              }
            }}
            wFull
            base
            disabled={isLoading}
          >
            {!email ? "Signup" : !codeSent ? "Send Code" : "Signup"}
          </Button>
        </div>
      </div>{" "}
      <div className={classes.bottomBar}>
        <Text PrimitiveTransparent60 sm textCenter>
          Already have an account?
        </Text>
        <Button transparent to="/login" className={classes.button}>
          Login
        </Button>
      </div>
    </AuthenticationModal>
  );
};

export default Signup;
